import { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { categoryColor, formatShare, trend } from './format';
import type { CategoryRow } from './types';

const TOP = 25;

type Props = {
  categories: CategoryRow[];
  selectedIds: string[];
  activeId?: string;
  locale: string;
  onToggle: (id: string) => void;
  onOpen: (id: string) => void;
};

export const CategoryList = ({ categories, selectedIds, activeId, locale, onToggle, onOpen }: Props) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? categories.filter((c) => c.name.toLowerCase().includes(q)) : categories;
  }, [categories, query]);
  const visible = showAll || query ? matches : matches.slice(0, TOP);

  const trendLabel = (c: CategoryRow) => {
    const tr = trend(c.requests, c.previousRequests);
    return tr.dir === 'new' || tr.dir === 'flat'
      ? t(`dnsCategories.trend.${tr.dir}`)
      : t(`dnsCategories.trend.${tr.dir}`, { pct: tr.pct });
  };

  return (
    <>
      <div className="tm-cats-hd">
        <span className="bd-field-label">{t('dnsCategories.list.title')}</span>
        <span className="bd-meta">
          <span className="bd-chip mint">{t('dnsCategories.list.selected', { count: selectedIds.length })}</span>{' '}
          {t('dnsCategories.list.count', { count: categories.length })}
        </span>
      </div>
      <div className="dc-list-note bd-meta">{t('dnsCategories.list.subtitle')}</div>
      <label className="bd-search tm-filter dc-list-search">
        <Search size={14} />
        <input
          type="search"
          value={query}
          placeholder={t('dnsCategories.list.search')}
          aria-label={t('dnsCategories.list.search')}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>

      <div className="tm-cats">
        {visible.length === 0 && <div className="bd-meta dc-no-match">{t('dnsCategories.list.noMatch')}</div>}
        {visible.map((c) => {
          const on = selectedIds.includes(c.id);
          const tr = trend(c.requests, c.previousRequests);
          return (
            <div
              key={c.id}
              className={`tm-cat ${on ? 'on' : ''} ${activeId === c.id ? 'dc-active' : ''}`}
              style={{ '--cat': categoryColor(c.id) } as React.CSSProperties}
            >
              <button
                type="button"
                className="tm-cat-check dc-check"
                role="checkbox"
                aria-checked={on}
                aria-label={t('dnsCategories.list.toggle', { name: c.name })}
                onClick={() => onToggle(c.id)}
              >
                {on && <Check size={11} strokeWidth={3} />}
              </button>
              <button type="button" className="tm-cat-body dc-cat-body" onClick={() => onOpen(c.id)}>
                <span className="tm-cat-row">
                  <span className="tm-cat-label">{c.name}</span>
                  <span className="tm-cat-count bd-mono">{c.requests.toLocaleString(locale)}</span>
                </span>
                <span className="tm-cat-bar">
                  <i style={{ width: `${Math.min(c.share * 100, 100)}%` }} />
                </span>
                <span className="dc-cat-meta bd-meta">
                  <span>{formatShare(c.share)}</span>
                  <span className={`dc-trend dc-trend-${tr.dir}`}>{trendLabel(c)}</span>
                </span>
              </button>
            </div>
          );
        })}
        {!query && matches.length > TOP && (
          <button type="button" className="bd-pill dc-show-all" onClick={() => setShowAll((v) => !v)}>
            {showAll ? t('dnsCategories.list.showTop') : t('dnsCategories.list.showAll', { count: matches.length })}
          </button>
        )}
      </div>
    </>
  );
};
